import React, { useState, useEffect, Fragment } from "react";
import cn from "classnames";
import {
	Text,
	Email,
	Phone,
	AddressFinder,
	AddressFull,
	Blank,
	Payment,
	CardDetails,
	GiftAid,
	Checkboxes,
	ContactPreferencesTable,
	Separator,
	UrlLocation,
	Select,
	DonationFrequencyAndAmount,
	ImageUpload,
	Password,
	Radio,
	DateField,
	Textarea,
	FindPost,
	Brickwall,
	SimpleHidden,
} from "./fields";
import { replaceTemplateVariables } from "../utils/templateUtils";

const FormStep = ({
	step,
	register,
	unregister,
	errors,
	watch,
	setValue,
	trigger,
	config,
	setSelectedPaymentMethod,
	selectedPaymentMethod,
	setPaymentMethods,
	paymentMethods,
	setCurrentStep,
}) => {
	// Track which fields have trigger endpoints to monitor
	const fieldsWithTriggers =
		step.sections?.flatMap((section) =>
			section.fields.filter(
				(field) => field.trigger_endpoint && field.trigger_endpoint_on_watch
			)
		) || [];

	// Track which fields have conditional logic
	const fieldsWithConditionals =
		step.sections?.flatMap((section) =>
			section.fields.filter((field) => field.conditional)
		) || [];

	// Monitor field values for automatic triggering
	useEffect(() => {
		// Check for existing values on mount
		const currentValues = watch();
		fieldsWithTriggers.forEach((field) => {
			const fieldValue = currentValues[field.slug];
			if (fieldValue && fieldValue.trim() !== "") {
				// Check if we've already triggered for this value to avoid infinite loops
				const responses = currentValues.responses || {};
				const lastResponse = responses[field.slug];

				if (!lastResponse || lastResponse.lastValue !== fieldValue) {
					handleTriggerEndpoint(field.slug, field.trigger_endpoint, fieldValue);
				}
			}
		});

		// Monitor for changes
		const subscription = watch((value, { name, type }) => {
			// Only proceed if a field with trigger endpoint changed
			if (name && type === "change") {
				const field = fieldsWithTriggers.find((f) => f.slug === name);
				if (field) {
					const fieldValue = value[name];

					// Only trigger if field has a value
					if (fieldValue && fieldValue.trim() !== "") {
						// Check if we've already triggered for this value to avoid infinite loops
						const responses = value.responses || {};
						const lastResponse = responses[name];

						if (!lastResponse || lastResponse.lastValue !== fieldValue) {
							handleTriggerEndpoint(name, field.trigger_endpoint, fieldValue);
						}
					}
				}
			}
		});

		return () => subscription.unsubscribe();
	}, [watch, fieldsWithTriggers]);

	// Extract configuration from props
	const formConfig = config?.config;
	const stripePublicKey = config?.stripe_public_key;
	const formData = watch();

	// Check if field should be conditionally shown (supports single-level AND/OR)
	const shouldShowField = (field) => {
		if (!field.conditional) return true;

		const getWatchedValue = (conditionalField) => {
			// Handle nested field access (e.g., "responses[email].exists")
			if (conditionalField.includes(".")) {
				const fieldParts = conditionalField.split(".");
				const baseField = fieldParts[0];
				const nestedPath = fieldParts.slice(1);
				let watchedValue = watch(baseField);
				for (const part of nestedPath) {
					if (watchedValue && typeof watchedValue === "object") {
						watchedValue = watchedValue[part];
					} else {
						watchedValue = undefined;
						break;
					}
				}
				return watchedValue;
			}
			return watch(conditionalField);
		};

		const evaluateSimple = (condition) => {
			const { field: conditionalField, value, operator } = condition;
			const watchedValue = getWatchedValue(conditionalField);
			switch (operator) {
				case "equals":
					return watchedValue === value;
				case "not_equals":
					return watchedValue !== value;
				case "contains":
					return (
						watchedValue &&
						typeof watchedValue.includes === "function" &&
						watchedValue.includes(value)
					);
				case "not_contains":
					return (
						watchedValue &&
						typeof watchedValue.includes === "function" &&
						!watchedValue.includes(value)
					);
				case "is_empty":
					return (
						!watchedValue ||
						watchedValue === "" ||
						watchedValue === null ||
						watchedValue === undefined
					);
				case "is_not_empty":
					return (
						watchedValue !== "" &&
						watchedValue !== null &&
						watchedValue !== undefined
					);
				default:
					return true;
			}
		};

		// Single-level group handling: { logic: 'AND'|'OR', conditions: [ ...simple conditions... ] }
		if (Array.isArray(field.conditional.conditions)) {
			const logic = (field.conditional.logic || "AND").toUpperCase();
			const results = field.conditional.conditions.map((c) =>
				evaluateSimple(c)
			);
			return logic === "OR" ? results.some(Boolean) : results.every(Boolean);
		}

		// Simple condition object
		return evaluateSimple(field.conditional);
	};

	const renderField = (field) => {
		// Don't render field if conditional logic says it should be hidden
		if (!shouldShowField(field)) {
			return null;
		}

		const commonProps = {
			id: field.slug,
			register: register,
			unregister: unregister,
			error: errors[field.slug],
			required: field.required,
			placeholder: field.placeholder,
			setValue: setValue,
			trigger: trigger,
			watch: watch,
			errors: errors,
			field: field,
			config: config, // Pass config down to all field components
			setCurrentStep: setCurrentStep,
		};

		const renderFieldContent = () => {
			switch (field.type) {
				case "brickwall":
					return <Brickwall {...commonProps} />;
				case "wysiwyg":
					return (
						<div
							className="field__wysiwyg"
							dangerouslySetInnerHTML={{
								__html: replaceTemplateVariables(field.content, [
									formData,
									formConfig,
								]),
							}}
						/>
					);
				case "text":
					return (
						<Text
							{...commonProps}
							placeholder={field.placeholder || "Enter text"}
						/>
					);
				case "date":
					return (
						<DateField
							{...commonProps}
							placeholder={field.placeholder || "dd/mm/yyyy"}
						/>
					);
				case "textarea":
					return (
						<Textarea
							{...commonProps}
							placeholder={field.placeholder || "Enter text"}
						/>
					);
				case "email":
					return <Email {...commonProps} />;
				case "password":
					return <Password {...commonProps} />;
				case "phone":
					return <Phone {...commonProps} />;
				case "address_full":
					return <AddressFull {...commonProps} />;
				case "address_finder":
					return <AddressFinder {...commonProps} countryDropdown={field.country_dropdown} />;
				case "select":
					return (
						<Select
							{...commonProps}
							options={field.options}
							selectBlankOption={field.selectBlankOption}
						/>
					);
				case "postcode":
					return <Postcode {...commonProps} />;
				case "blank":
					return <Blank {...commonProps} />;
				case "payment":
					return (
						<Payment
							{...commonProps}
							field={field}
							formData={formData}
							config={config}
							stripePublicKey={stripePublicKey}
							setSelectedPaymentMethod={setSelectedPaymentMethod}
							selectedPaymentMethod={selectedPaymentMethod}
							setPaymentMethods={setPaymentMethods}
							paymentMethods={paymentMethods}
						/>
					);
				case "card_details":
					return <CardDetails {...commonProps} />;
				case "gift_aid":
					return <GiftAid {...commonProps} formData={formData} field={field} />;
				case "checkboxes":
					return <Checkboxes {...commonProps} field={field} />;
				case "contact_preferences_table":
					return (
						<ContactPreferencesTable {...commonProps} field={field} />
					);
				case "radio":
					return <Radio {...commonProps} field={field} />;
				case "separator":
					return (
						<Separator {...commonProps} separator_type={field.classname} />
					);
				case "url_location":
					return <UrlLocation {...commonProps} />;
				case "hidden":
					return <SimpleHidden {...commonProps} />;
				case "donation_frequency_and_amount":
					return (
						<DonationFrequencyAndAmount
							{...commonProps}
							field={field}
							config={formConfig}
							frequencyStyle={field.frequency_style || "pills"}
						/>
					);
				case "image_upload":
					return (
						<ImageUpload
							{...commonProps}
							multi={field.multiple || false}
							crop={field.crop || false}
							maxFiles={field.maxFiles || 5}
							maxSize={field.maxSize || 5242880}
							watch={watch}
							setValue={setValue}
							acceptedFileTypes={
								field.acceptedFileTypes || {
									"image/*": [".jpeg", ".jpg", ".png", ".gif", ".webp"],
								}
							}
							placeholder={
								field.placeholder ||
								"Drag & drop images here, or click to select files"
							}
						/>
					);
				case "find_post":
					return <FindPost {...commonProps} field={field} />;
				default:
					return null;
			}
		};

		const fieldTriggered =
			field.trigger_endpoint &&
			!field.trigger_endpoint_on_watch &&
			watch("responses") &&
			watch("responses")[field.slug];

		return (
			<div
				className={cn(
					`field ${field.type}-field`,
					field.slug,
					field.classname || ""
				)}
				key={field.slug}
			>
				{!fieldTriggered && (
					<>
						{field.title && (
							<label
								className={cn(
									"field__label",
									field.hide_label && "visually-hidden sr-only"
								)}
								htmlFor={field.slug}
							>
								{replaceTemplateVariables(field.title, formData)}
								{field.required && <span className="required">*</span>}
							</label>
						)}

						{field.description && (
							<div
								className="field__description"
								dangerouslySetInnerHTML={{
									__html: replaceTemplateVariables(field.description, [
										formData,
										formConfig,
									]),
								}}
							/>
						)}

						{renderFieldContent()}
						{field.trigger_endpoint && !field.trigger_endpoint_on_watch && (
							<button
								type="button"
								className="btn btn--solid btn--indigo"
								onClick={() =>
									handleTriggerEndpoint(field.slug, field.trigger_endpoint)
								}
							>
								Submit
							</button>
						)}
					</>
				)}
				{fieldTriggered && field.show_response !== false && (
					<div className="field__response">
						{watch("responses")[field.slug].message}
					</div>
				)}

				{/* Show response underneath for trigger_endpoint_on_watch fields */}
				{field.trigger_endpoint_on_watch &&
					field.show_response !== false &&
					watch("responses") &&
					watch("responses")[field.slug] && (
						<div className="field__response field__response--below">
							{watch("responses")[field.slug].message}
						</div>
					)}
			</div>
		);
	};

	const handleTriggerEndpoint = async (fieldSlug, endpoint, value = null) => {
		const payload = value ? { value } : { formData };

		const response = await fetch(endpoint, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(payload),
		});
		const data = await response.json();

		//add to responses value with lastValue to prevent infinite loops
		setValue("responses", {
			...formData.responses,
			[fieldSlug]: {
				...data,
				lastValue: value,
			},
		});

		if (data.success) {
			return data.message;
		}
		return null;
	};

	// Render the step content
	const renderStepContent = () => (
		<div className="form-step">
			{step.image && (
				<img
					src={step.image}
					alt={step.title}
					className={`form-step__image ${step?.remove_from_progress ? "no-progress-bar" : ""}`}
				/>
			)}
			{!step.hide_step_header && (
				<>
					<div className="form-step__header field-section-wrapper container">
						{step.title && !step.hide_title && (
							<h1>
								{replaceTemplateVariables(step.title, [formData, formConfig])}
							</h1>
						)}
						{step.description && (
							<div
								className="field__description above"
								dangerouslySetInnerHTML={{
									__html: replaceTemplateVariables(step.description, [
										formData,
										formConfig,
									]),
								}}
							/>
						)}
					</div>
					{!formConfig.hide_separators && <Separator separator_type="wide" />}
				</>
			)}
			{step.sections &&
			(() => {
				const visibleSections = step.sections
				.map((section) => ({
					section,
					visibleFields: Array.isArray(section.fields)
					? section.fields.filter((field) => shouldShowField(field))
					: [],
				}))
				.filter(({ visibleFields }) => visibleFields.length > 0);
				
				return visibleSections.map(({ section, visibleFields }, index) => (
				<Fragment key={index}>
					<div className={`field-section-wrapper ${section.classname}`}>
						{section.title && !section.hide_title && <h2>{section.title}</h2>}
						{section.description && (
							<p className="field__description">{section.description}</p>
						)}
						{visibleFields.map((field) => (
							<Fragment key={field.slug}>{renderField(field)}</Fragment>
						))}
					</div>
					{index < visibleSections.length - 1 && !formConfig.hide_separators && (
						<Separator separator_type="wide" />
					)}
				</Fragment>
				));
			})()}
		</div>
	);

	// Render normally
	return renderStepContent();
};

export default FormStep;
